import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, X, Minus, LayoutGrid, Plus, Trash2, Camera, History } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  displayed: { icon: Check, label: "Displayed", bg: "bg-emerald-500", border: "border-emerald-600", text: "text-emerald-700" },
  not_displayed: { icon: X, label: "Not displayed", bg: "bg-red-500", border: "border-red-600", text: "text-red-700" },
  unchecked: { icon: Minus, label: "Not checked", bg: "bg-slate-400", border: "border-slate-500", text: "text-slate-600" },
};

export default function FloorDisplay({ customerId }) {
  const [data, setData] = useState(null);
  const [openHistory, setOpenHistory] = useState(false);
  const [openReason, setOpenReason] = useState(null); // model_id when marking cross
  const [addUnitFor, setAddUnitFor] = useState(null); // model row when adding unit

  const load = async () => {
    const { data } = await api.get(`/customers/${customerId}/floor-display`);
    setData(data);
  };
  useEffect(() => { load(); }, [customerId]);

  const setStatus = async (m, status, extra = {}) => {
    try {
      await api.post("/floor-display/status", {
        customer_id: customerId, model_id: m.model_id, status, ...extra,
      });
      toast.success(`${m.model_name}: ${STATUS_META[status].label}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const addUnit = async (m, unit) => {
    try {
      const payload = { customer_id: customerId, model_id: m.model_id, ...unit };
      // Upload photo first if file present
      if (unit._file) {
        const fd = new FormData();
        fd.append("file", unit._file);
        fd.append("customer_id", customerId);
        fd.append("category", "Floor Display");
        fd.append("notes", `${m.model_name} — ${unit.size}`);
        const { data } = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        payload.photo_url = data.id;
      }
      delete payload._file;
      await api.post("/floor-display/unit", payload);
      toast.success("Floor unit added");
      setAddUnitFor(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const removeUnit = async (m, idx) => {
    if (!window.confirm("Remove this floor unit?")) return;
    await api.delete("/floor-display/unit", { params: { customer_id: customerId, model_id: m.model_id, index: idx } });
    toast.success("Unit removed"); load();
  };

  if (!data) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="floor-display-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-[#003DA5]" />
          <h3 className="text-lg font-heading font-semibold">Floor Display</h3>
        </div>
        <button onClick={() => setOpenHistory(!openHistory)} className="text-xs text-[#003DA5] font-semibold flex items-center gap-1 hover:underline" data-testid="toggle-fd-history">
          <History className="w-3.5 h-3.5" /> {openHistory ? "Hide" : "Show"} history
        </button>
      </div>

      <div className="space-y-6">
        {data.categories.map(cat => (
          <div key={cat.category_name}>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">{cat.category_name}</div>
            <div className="grid gap-3">
              {cat.models.map(m => (
                <ModelRow key={m.model_id} m={m}
                          onTick={() => setStatus(m, "displayed")}
                          onCross={() => setOpenReason(m)}
                          onDash={() => setStatus(m, "unchecked")}
                          onAddUnit={() => setAddUnitFor(m)}
                          onRemoveUnit={(idx) => removeUnit(m, idx)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {openReason && (
        <ReasonModal m={openReason} onClose={() => setOpenReason(null)}
                     onSave={(extra) => { setStatus(openReason, "not_displayed", extra); setOpenReason(null); }} />
      )}
      {addUnitFor && (
        <AddUnitModal m={addUnitFor} onClose={() => setAddUnitFor(null)} onSave={(u) => addUnit(addUnitFor, u)} />
      )}
      {openHistory && <FDHistory customerId={customerId} />}
    </div>
  );
}

function ModelRow({ m, onTick, onCross, onDash, onAddUnit, onRemoveUnit }) {
  const stMeta = STATUS_META[m.status] || STATUS_META.unchecked;
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={`fd-model-${m.model_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold text-[#001F54]">{m.model_name}</div>
          <div className="text-xs text-slate-500">{m.category_name} {m.model_sku ? `• ${m.model_sku}` : ""}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">Sizes: {m.available_sizes.join(", ") || "—"}</div>
          {m.last_verified_at && (
            <div className="text-[10px] text-slate-400 mt-1">Last verified: {new Date(m.last_verified_at).toLocaleString()} by {m.verified_by_name}</div>
          )}
          {m.reason_not_displayed && <div className="text-xs text-red-600 mt-1">Reason: {m.reason_not_displayed}{m.competitor ? ` (competitor: ${m.competitor})` : ""}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <StatusBtn active={m.status === "displayed"} onClick={onTick} tone="green" testid={`fd-tick-${m.model_id}`} Icon={Check} />
          <StatusBtn active={m.status === "not_displayed"} onClick={onCross} tone="red" testid={`fd-cross-${m.model_id}`} Icon={X} />
          <StatusBtn active={m.status === "unchecked"} onClick={onDash} tone="grey" testid={`fd-dash-${m.model_id}`} Icon={Minus} />
        </div>
      </div>

      {m.status === "displayed" && (
        <div className="mt-3 border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-semibold text-slate-500">Floor units ({m.units.length})</div>
            <button onClick={onAddUnit} className="text-xs text-[#003DA5] font-semibold hover:underline flex items-center gap-1" data-testid={`fd-add-unit-${m.model_id}`}>
              <Plus className="w-3 h-3" /> Add size
            </button>
          </div>
          {m.units.length === 0 ? (
            <div className="text-xs text-slate-400 italic">No units added yet — tap "Add size" to record floor stock.</div>
          ) : (
            <ul className="space-y-1.5">
              {m.units.map((u, i) => (
                <li key={i} className="flex items-center justify-between p-2 rounded bg-[#F3F7FC] text-sm" data-testid={`fd-unit-${m.model_id}-${i}`}>
                  <div>
                    <span className="font-semibold">{u.size}</span>
                    <span className="text-slate-500"> × {u.quantity}</span>
                    {u.location && <span className="text-slate-400 text-xs"> • {u.location}</span>}
                    {u.display_condition && u.display_condition !== "Good" && <span className={`text-[10px] px-1.5 py-0.5 rounded ml-1 ${u.display_condition === "Damaged" || u.display_condition === "Needs replacement" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{u.display_condition}</span>}
                    {u.notes && <div className="text-xs text-slate-500">{u.notes}</div>}
                  </div>
                  <button onClick={() => onRemoveUnit(i)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`fd-unit-remove-${m.model_id}-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBtn({ active, onClick, tone, testid, Icon }) {
  const map = {
    green: active ? "bg-emerald-500 border-emerald-600 text-white shadow-lg" : "bg-white border-slate-200 text-slate-400 hover:border-emerald-400",
    red: active ? "bg-red-500 border-red-600 text-white shadow-lg" : "bg-white border-slate-200 text-slate-400 hover:border-red-400",
    grey: active ? "bg-slate-500 border-slate-600 text-white shadow-lg" : "bg-white border-slate-200 text-slate-400 hover:border-slate-400",
  };
  return (
    <button onClick={onClick} type="button" data-testid={testid}
      className={`w-14 h-14 rounded-xl grid place-items-center border-2 transition ${map[tone]}`}>
      <Icon className="w-7 h-7" strokeWidth={3} />
    </button>
  );
}

function ReasonModal({ m, onClose, onSave }) {
  const [f, setF] = useState({ reason_not_displayed: "", competitor: "", notes: "" });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-5 border-b"><h3 className="text-lg font-heading font-semibold">Why isn't {m.model_name} displayed?</h3></div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs uppercase font-semibold text-slate-500">Reason</label>
            <select value={f.reason_not_displayed} onChange={e => setF({ ...f, reason_not_displayed: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="cross-reason">
              <option value="">— select —</option>
              <option>Previous floor stock removed</option>
              <option>Competitor model displayed</option>
              <option>Sales opportunity — never displayed</option>
              <option>Awaiting delivery</option>
              <option>Other</option>
            </select>
          </div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Competitor (if any)</label>
            <input value={f.competitor} onChange={e => setF({ ...f, competitor: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="cross-competitor" /></div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Notes</label>
            <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1 w-full p-3 border rounded-lg" /></div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border rounded-lg">Cancel</button>
          <button onClick={() => onSave(f)} className="mlily-btn h-10 px-5 rounded-lg font-semibold" data-testid="cross-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function AddUnitModal({ m, onClose, onSave }) {
  const [f, setF] = useState({ size: m.available_sizes[0] || "Standard", quantity: 1, location: "", display_condition: "Good", notes: "", _file: null });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b"><h3 className="text-lg font-heading font-semibold">Add {m.model_name} floor unit</h3></div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs uppercase font-semibold text-slate-500">Size *</label>
            <select value={f.size} onChange={e => setF({ ...f, size: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="unit-size">
              {(m.available_sizes.length ? m.available_sizes : ["Standard"]).map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs uppercase font-semibold text-slate-500">Quantity *</label>
              <input type="number" min="1" value={f.quantity} onChange={e => setF({ ...f, quantity: Number(e.target.value) })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="unit-qty" /></div>
            <div><label className="text-xs uppercase font-semibold text-slate-500">Condition</label>
              <select value={f.display_condition} onChange={e => setF({ ...f, display_condition: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="unit-condition">
                <option>Good</option><option>Fair</option><option>Damaged</option><option>Needs replacement</option>
              </select></div>
          </div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Location</label>
            <input value={f.location} onChange={e => setF({ ...f, location: e.target.value })} placeholder="e.g. Front window" className="mt-1 w-full h-11 px-3 border rounded-lg" data-testid="unit-location" /></div>
          <div><label className="text-xs uppercase font-semibold text-slate-500">Notes</label>
            <input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="mt-1 w-full h-11 px-3 border rounded-lg" /></div>
          <label className="inline-flex items-center gap-2 h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm cursor-pointer hover:border-[#003DA5]" data-testid="unit-photo-btn">
            <Camera className="w-4 h-4" /> {f._file ? f._file.name : "Snap photo (optional)"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => setF({ ...f, _file: e.target.files[0] })} />
          </label>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 border rounded-lg">Cancel</button>
          <button onClick={() => onSave(f)} className="mlily-btn h-10 px-5 rounded-lg font-semibold" data-testid="unit-save">Add unit</button>
        </div>
      </div>
    </div>
  );
}

function FDHistory({ customerId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/floor-display/history", { params: { customer_id: customerId } }).then(r => setItems(r.data));
  }, [customerId]);
  return (
    <div className="mt-4 border-t pt-4">
      <div className="text-xs uppercase font-semibold text-slate-500 mb-2">Change History</div>
      {items.length === 0 ? <div className="text-xs text-slate-400">No changes yet.</div> : (
        <ul className="space-y-1 max-h-80 overflow-y-auto">
          {items.map(h => (
            <li key={h.id} className="text-xs py-1.5 border-b border-slate-100" data-testid={`fd-hist-${h.id}`}>
              <div><span className="font-semibold">{h.event.replace(/_/g, " ")}</span> — {h.model_name}</div>
              <div className="text-slate-500">{h.changed_by_name} • {new Date(h.changed_at).toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
